// ============================================================================
// cameraFrame.js — registry-driven camera framing for search-result focus.
//
// Replaces the old focusCameraOn() approach (instant teleport + 6s monitor loop
// that fought the user). This engine animates a smooth eased tween to a
// deterministic framing computed per mesh, then releases control immediately.
//
// Framing rules:
//   target  : entry.camera.target override, else mesh bounding-box center
//   zoom    : pinhole fit from bbox sphere radius + FOV + viewport aspect (~60%)
//   angle   : entry.camera.orbit override, else along mesh's thinnest bbox axis
//   label   : shift target along camera-right so the label side has room
//   motion  : delegated to <model-viewer>'s native damper (interpolationDecay),
//             bumped during focus for a cinematic glide — no manual tween/jump
// ============================================================================

import * as THREE from 'three';

const DEFAULT_DURATION = 850;   // ms waited for the damper to settle before the label fires
const FILL = 0.6;           // mesh fills ~60% of the limiting viewport dimension
const LABEL_OFFSET = 0.12;  // fraction of distance the target shifts for label room
const MIN_DISTANCE = 0.15;
const MAX_DISTANCE = 8;
// <model-viewer> natively damps camera orbit + target toward any new goal
// (interpolationDecay, default 50ms). We bump it during a focus move for a
// longer, more cinematic glide, then restore it so the user's own input stays
// responsive. The damper is the sole source of motion — no manual rAF tween.
const FOCUS_INTERPOLATION_DECAY = 150;

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const toRad = (d) => (d * Math.PI) / 180;
const toDeg = (r) => (r * 180) / Math.PI;

function parseOrbit(str) {
  if (!str) return { theta: 0, phi: 80, radius: 1 };
  const parts = String(str).replace(/deg|m/gi, '').trim().split(/\s+/).map(parseFloat);
  return { theta: parts[0] || 0, phi: parts[1] || 80, radius: parts[2] || 1 };
}

// Choose a viewing angle along the mesh's thinnest bounding-box axis so flat /
// slab parts are seen face-on rather than edge-on.
function thinnestAxisOrbit(size) {
  const { x, y, z } = size;
  if (x <= y && x <= z) return { theta: 90, phi: 80 };  // look along X
  if (z <= y) return { theta: 0, phi: 80 };             // look along Z (front)
  return { theta: 0, phi: 15 };                          // y thinnest -> top-down
}

// Brain-specific viewing angle. The Brain.glb model places the LEFT hemisphere
// on +X and the RIGHT hemisphere on -X (verified against systemPositions.js).
//
// Surface parts must be seen from their OUTER (lateral) face -> camera on the
// SAME side as the part, at a near-horizontal phi (~72) for a natural 3/4 view.
//
// Interior (reveal:'split') parts sit on the MEDIAL wall, exposed only after the
// hemispheres separate along X (SPLIT_DISTANCE = 0.12m each, ~0.24m gap). The
// medial faces point sideways toward each other across the gap. A steep phi
// (top-down) sees only the brain membrane from above; the medial wall is
// edge-on and invisible. Instead we use a moderate phi (~65) so the camera sits
// BETWEEN the two halves at a natural 3/4 angle looking INTO the gap at the
// target hemisphere's medial face. The opposite hemisphere is behind the
// camera and never occludes. A contralateral theta bias (~50) places the camera
// near the gap center (x~0) rather than directly in line with the part.
// Using entry.side (reliable bilateral marker) avoids depending on a tiny X
// offset (medulla is ~0.004m).
const BRAIN_SURFACE_THETA = 65;
const BRAIN_SURFACE_PHI = 72;
const BRAIN_SPLIT_THETA = 50;   // azimuth bias toward gap center (contralateral)
const BRAIN_SPLIT_PHI = 65;     // elevated 3/4 — looks INTO the gap, not down on it
function brainSideOrbit(side, isSplit) {
  // Midline structures (no side) get a generic front-ish view.
  if (side !== 'left' && side !== 'right') {
    return { theta: 20, phi: BRAIN_SURFACE_PHI };
  }
  const onLeftSide = side === 'left';            // left hemisphere == +X
  if (isSplit) {
    // Contralateral side (the gap), looking INTO it at the medial wall.
    const camOnLeft = !onLeftSide;
    const theta = camOnLeft ? BRAIN_SPLIT_THETA : -BRAIN_SPLIT_THETA;
    return { theta, phi: BRAIN_SPLIT_PHI };
  }
  // Surface: ipsilateral side to see the lateral (outer) face.
  const theta = onLeftSide ? BRAIN_SURFACE_THETA : -BRAIN_SURFACE_THETA;
  return { theta, phi: BRAIN_SURFACE_PHI };
}

// View straight-on along a surface normal (used when a click point is known).
function normalOrbit(normal) {
  const n = normal.clone().normalize();
  const phi = clamp(toDeg(Math.acos(clamp(n.y, -1, 1))), 10, 170);
  const theta = toDeg(Math.atan2(n.x, n.z));
  return { theta, phi };
}

// model-viewer spherical -> camera "right" vector in world space (screen-right).
function orbitRightVector(thetaDeg, phiDeg) {
  const t = toRad(thetaDeg);
  const p = toRad(phiDeg);
  const radial = new THREE.Vector3(
    Math.sin(p) * Math.sin(t),
    Math.cos(p),
    Math.sin(p) * Math.cos(t)
  );
  const viewDir = radial.clone().multiplyScalar(-1);
  const up = new THREE.Vector3(0, 1, 0);
  const right = new THREE.Vector3().crossVectors(viewDir, up);
  if (right.lengthSq() < 1e-8) right.set(1, 0, 0);
  return right.normalize();
}

export function frameMesh({
  mv,
  mesh,
  entry,
  hitNormal = null,
  side = null,
  duration = DEFAULT_DURATION,
  onSettled = null,
}) {
  if (!mv || !mesh) { onSettled && onSettled({}); return; }

  // Force the whole subtree to recompute world matrices so the bbox reflects
  // the current (e.g. post-split) transforms. updateWorldMatrix(true, true)
  // refreshes both ancestors and descendants; Box3.setFromObject otherwise may
  // read stale matrixWorld on children, leaving the anchor at a pre-split spot.
  mesh.updateWorldMatrix(true, true);
  const box = new THREE.Box3().setFromObject(mesh);
  if (!isFinite(box.min.x) || box.isEmpty()) { onSettled && onSettled({}); return; }

  const center = new THREE.Vector3();
  const size = new THREE.Vector3();
  box.getCenter(center);
  box.getSize(size);

  const sphere = new THREE.Sphere();
  box.getBoundingSphere(sphere);
  const sphereRadius = Math.max(sphere.radius, 1e-3);

  const cam = (entry && entry.camera) || {};
  const isSplit = !!(entry && entry.reveal === 'split');

  // 1. Camera pivot (orbit center) + label anchor (hotspot dot).
  //    For both surface and split brain parts we pivot on the part itself so it
  //    stays centered in frame. (Previously split pivoted on the whole-brain
  //    bbox, which left the tiny interior part off to the side / "lost".)
  //
  //    anchor = where the hotspot dot sits. This MUST match the camera target
  //    (pre-label-offset) so the dot agrees with where the camera is looking.
  //    Using the bbox center here caused the Phase-1 bug: for bones the override
  //    target (vertex centroid) disagrees with the bbox center, so the label
  //    drifted to the wrong spot.
  let pivot;
  if (Array.isArray(cam.target) && cam.target.length >= 3) {
    pivot = new THREE.Vector3(cam.target[0], cam.target[1], cam.target[2]);
  } else {
    pivot = center.clone();
  }
  const anchor = pivot.clone();

  // 2. Viewing angle
  let orbit;
  if (cam.orbit) {
    const o = parseOrbit(cam.orbit + ' 0m');
    orbit = { theta: o.theta, phi: o.phi };
  } else if (hitNormal) {
    orbit = normalOrbit(hitNormal);
  } else if (entry && entry.system === 'brain') {
    // Side-aware: lateral face for surface parts, through-the-gap for split.
    orbit = brainSideOrbit(entry.side, isSplit);
  } else {
    orbit = thinnestAxisOrbit(size);
  }

  // Side-aware orbit: mirror the viewing angle across the sagittal plane
  // (negate theta) for right-side parts so the camera approaches from the
  // mirrored position of its left counterpart. Left parts keep the default
  // angle. Only applied when there is no explicit orbit override (those are
  // already tuned), no hitNormal (clicks are inherently side-aware), and NOT
  // for brain parts (brainSideOrbit() already computes side-aware theta —
  // applying this here would double-negate right-side split parts, placing
  // the camera outside the brain instead of in the gap).
  if (side && !cam.orbit && !hitNormal && !(entry && entry.system === 'brain')) {
    if (side === 'right') {
      orbit.theta = -orbit.theta;
    }
  }

  // 3. View-facing normal: radial direction from target toward camera, so the
  //    hotspot dot/label faces the camera regardless of viewing angle.
  const nt = toRad(orbit.theta);
  const np = toRad(orbit.phi);
  const viewNormal = new THREE.Vector3(
    Math.sin(np) * Math.sin(nt),
    Math.cos(np),
    Math.sin(np) * Math.cos(nt)
  );

  // 4. Fit distance (pinhole). Distance so the bounding sphere fills `FILL` of
  //    the smaller viewport dimension, accounting for FOV and aspect. The
  //    registry `cam.distance` override takes precedence (used by split parts to
  //    frame the whole brain rather than the tiny structure).
  const fovDeg = parseFloat(mv.getAttribute('field-of-view')) || 45;
  const fov = toRad(fovDeg);
  const rect = mv.getBoundingClientRect();
  const aspect = rect.width && rect.height ? rect.width / rect.height : 1;
  let distance = sphereRadius / (FILL * Math.tan(fov / 2) * Math.min(1, aspect));
  distance = clamp(distance, MIN_DISTANCE, MAX_DISTANCE);
  if (typeof cam.distance === 'number' && isFinite(cam.distance)) {
    distance = clamp(cam.distance, MIN_DISTANCE, MAX_DISTANCE);
  }

  // ---- [brain-debug] diagnostic: pivot vs anchor ----
  const __src = cam.orbit ? 'OVERRIDE' : hitNormal ? 'normal'
    : (entry && entry.system === 'brain') ? 'brainSide' : 'thinnestAxis';
  console.log('[brain-debug] frameMesh', {
    mesh: mesh.name,
    reveal: entry && entry.reveal,
    source: __src,
    goalOrbit: { theta: orbit.theta, phi: orbit.phi },
    distance,
    anchor: { x: +anchor.x.toFixed(4), y: +anchor.y.toFixed(4), z: +anchor.z.toFixed(4) },
    pivot: { x: +pivot.x.toFixed(4), y: +pivot.y.toFixed(4), z: +pivot.z.toFixed(4) },
    partSize: { x: +size.x.toFixed(4), y: +size.y.toFixed(4), z: +size.z.toFixed(4) },
  });

  // 5. Label-room offset: shift the camera pivot along camera-right so the part
  //    sits on the side opposite the label. Skipped for split parts (the camera
  //    already looks into the gap from the opposite side; the dot's `anchor`
  //    carries the label position).
  const sign = side === 'left' ? -1 : side === 'right' ? 1 : 0;
  if (sign !== 0 && !isSplit) {
    const right = orbitRightVector(orbit.theta, orbit.phi);
    pivot = pivot.clone().add(right.multiplyScalar(distance * LABEL_OFFSET * sign));
  }

  animateOrbitTo(mv, pivot, orbit.theta, orbit.phi, distance, duration, () => {
    onSettled && onSettled({ anchor, normal: viewNormal });
  });
}

function animateOrbitTo(mv, goalTarget, goalTheta, goalPhi, goalRadius, duration, onSettled) {
  // Delegate the motion to <model-viewer>'s NATIVE damper. The element already
  // eases both the orbit and the target toward any new goal; our previous manual
  // rAF tween fought that damper (double-smoothing) and the per-frame
  // jumpCameraToGoal() stuttered / "cut through" the move. By setting the goal
  // once and letting the damper fly there, the transition is genuinely smooth.
  //
  // interpolationDecay is bumped for a longer cinematic glide, then restored so
  // the user's own orbit/zoom input stays responsive afterwards.
  const prevDecay = mv.interpolationDecay;
  try { mv.interpolationDecay = FOCUS_INTERPOLATION_DECAY; } catch (err) {}

  mv.cameraOrbit = `${goalTheta.toFixed(3)}deg ${goalPhi.toFixed(3)}deg ${goalRadius.toFixed(4)}m`;
  mv.cameraTarget = `${goalTarget.x.toFixed(4)}m ${goalTarget.y.toFixed(4)}m ${goalTarget.z.toFixed(4)}m`;
  if (mv.requestUpdate) mv.requestUpdate();

  // Wait for the damper to settle (~5 decay constants), then restore the decay
  // and reveal the label.
  setTimeout(() => {
    try { mv.interpolationDecay = prevDecay; } catch (err) {}
    onSettled && onSettled();
  }, duration);
}
